import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AuthProvider, useAuth, UserManager } from 'oidc-react';
import { SIGNUP_USER_MANAGER_CONFIG, USER_MANAGER_CONFIG } from './config';
import { LoginComplete } from './App/LoginComplete';
import styled, { createGlobalStyle } from 'styled-components';
import { Interview } from './App/Interview';
import { TabItem, Tabs } from './Components/Tabs';
import { USER_MANAGEMENT_ASSET_NAMES } from '../iac/constants';
import { SYM } from './Components/MaterialSymbol';
import { PurposePreface } from './App/Interview/PurposePreface';
import { ApplicationStateProvider, getEasyLayout, Route, useRouteContext } from '@resistdesign/voltra/app/utils';
import { Login } from './App/Login';
import { Documents } from './App/Documents';
import { Profile } from './App/Profile';
import { Admin } from './App/Admin';
import { AudioRecordingScreen } from './App/Interview/AudioRecordingScreen';
import { InterviewComplete } from './App/Interview/InterviewComplete';
import { Theme } from './App/Theme';
// @ts-ignore
import Logo from 'url:./Assets/header-logo.svg';
import { DEFAULT_POSITION_ID, PositionID } from '../common/Positions/Constants';

let RELOADED_SINCE_SIGN_UP_ATTEMPT = true;
let REDIRECTED_TO_SIGN_UP = false;
const SIGN_UP_AFTER_LOGOUT_FLAG = 'SIGN_UP_AFTER_LOGOUT_FLAG';
const SIGN_UP_POSITION_AFTER_LOGOUT = 'SIGN_UP_POSITION_AFTER_LOGOUT';

const { post_logout_redirect_uri: SIGNOUT_URL } = USER_MANAGER_CONFIG;
const USER_MANAGER = new UserManager(USER_MANAGER_CONFIG as any);
const SIGNUP_USER_MANAGER = new UserManager(SIGNUP_USER_MANAGER_CONFIG as any);
const HEADER_TAB_DATA: {
  label: string;
  path: string;
}[] = [
  {
    label: 'Interview',
    path: '/interview',
  },
  {
    label: 'Admin',
    path: '/admin',
  },
];
const HEADER_TAB_ITEMS: TabItem[] = HEADER_TAB_DATA.map(({ label, path }) => ({
  label,
  onClick: () => history.pushState({}, '', path),
}));

const GlobalStyle = createGlobalStyle`
  html,
  body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
`;
const LogoImg = styled.img.attrs({
  src: Logo,
  className: 'logo-img',
})`
  &.logo-img {
    max-height: 100%;
  }
`;
const LayoutBase = styled.div`
  width: 100vw;
  height: 100vh;
  padding: 1em;
  gap: 1em;
`;
const {
  layout: Layout,
  areas: { Header, Main },
} = getEasyLayout(
  LayoutBase,
  styled.div`
    overflow: hidden;
  `
)`
  header, 1fr
  main, 12fr
  \\ 1fr
`;
const {
  layout: HeaderControls,
  areas: { Title: BaseTitle, Controls, User },
} = getEasyLayout(
  styled(Header)`
    gap: 1em;
  `,
  styled.div`
    flex: 1 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: stretch;
  `
)`
  title controls user, 1fr
  \\ 10fr 2fr 1fr
`;
const Title = styled(BaseTitle)`
  overflow: hidden;
`;
const UserControls = styled(User)`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1em;
`;

const InnerApp: FC = () => {
  const isSigningUpAfterLogout: boolean = localStorage.getItem(SIGN_UP_AFTER_LOGOUT_FLAG) === SIGN_UP_AFTER_LOGOUT_FLAG;
  const storedPositionIDFromURL = localStorage.getItem(SIGN_UP_POSITION_AFTER_LOGOUT) || undefined;
  const positionIDFromURL = useMemo<string | undefined>(() => {
    return isSigningUpAfterLogout
      ? storedPositionIDFromURL
      : new URLSearchParams(location.search).get('position_id') || undefined;
  }, []);
  const [positionID, setPositionID] = useState<PositionID | undefined>(undefined);
  const { userData, signOut, signIn, isLoading: authIsLoading = false } = useAuth();
  const onLogout = useCallback(async () => {
    await signOut();
    window.location.href = SIGNOUT_URL as string;
  }, [signOut]);
  const goToDocumentAcceptance = useCallback(() => {
    if (userData) {
      RELOADED_SINCE_SIGN_UP_ATTEMPT = false;
      localStorage.setItem(SIGN_UP_AFTER_LOGOUT_FLAG, SIGN_UP_AFTER_LOGOUT_FLAG);
      localStorage.setItem(SIGN_UP_POSITION_AFTER_LOGOUT, positionIDFromURL || '');
      onLogout();
    } else {
      history.pushState({}, '', '/sign-up');
    }
  }, [userData, onLogout, positionIDFromURL]);
  const onCancelAcceptance = useCallback(() => {
    history.pushState({}, '', '/');
  }, []);
  const signUp = useCallback(async () => {
    const realPositionID: PositionID =
      positionIDFromURL && positionIDFromURL in PositionID ? (positionIDFromURL as PositionID) : DEFAULT_POSITION_ID;

    await SIGNUP_USER_MANAGER.signinRedirect({
      state: { position_id: realPositionID },
    });
  }, [positionIDFromURL]);
  const userGroups = useMemo<string[]>(() => {
    const { profile = {} } = userData || {};
    const { ['cognito:groups']: groups = [] } = profile as any;

    return groups;
  }, [userData]);
  const isAdmin = useMemo(() => userGroups.includes(USER_MANAGEMENT_ASSET_NAMES.GROUPS.ADMIN), [userGroups]);
  const { access_token: authorization = '' } = userData || {};
  const { currentWindowPath = '' } = useRouteContext();
  const isLoginPath = currentWindowPath === '/';
  const selectedTabIndex = useMemo<number>(() => {
    for (let i = 0; i < HEADER_TAB_DATA.length; i++) {
      const { path } = HEADER_TAB_DATA[i];

      if (currentWindowPath.startsWith(path)) {
        return i;
      }
    }

    return -1;
  }, [currentWindowPath]);

  useEffect(() => {
    if (!authIsLoading) {
      if (RELOADED_SINCE_SIGN_UP_ATTEMPT && isSigningUpAfterLogout) {
        localStorage.removeItem(SIGN_UP_AFTER_LOGOUT_FLAG);
        localStorage.removeItem(SIGN_UP_POSITION_AFTER_LOGOUT);
        REDIRECTED_TO_SIGN_UP = true;
        history.pushState({}, '', '/sign-up');
      } else if (!REDIRECTED_TO_SIGN_UP && !authorization) {
        history.pushState({}, '', '/');
      }
    }
  }, [authIsLoading, isSigningUpAfterLogout, authorization]);

  return (
    <Layout>
      <HeaderControls>
        <Title>
          {!isLoginPath ? (
            <LogoImg alt="AdvisorInterview by Lead-Advisor" title="AdvisorInterview by Lead-Advisor" />
          ) : undefined}
        </Title>
        <Controls>
          {isAdmin ? <Tabs tabItems={HEADER_TAB_ITEMS} selectedIndex={selectedTabIndex} /> : undefined}
        </Controls>
        <UserControls>
          {authorization ? (
            <SYM title="Logout" style={{ cursor: 'pointer' }} onClick={onLogout}>
              logout
            </SYM>
          ) : undefined}
        </UserControls>
      </HeaderControls>
      <Main data-id="MAIN">
        <Route exact>
          <Login onSignIn={signIn} goToDocumentAcceptance={goToDocumentAcceptance} />
        </Route>
        <Route path="/sign-up">
          <Documents onAccept={signUp} onCancel={onCancelAcceptance} />
        </Route>
        <Route path="/login-complete">
          <LoginComplete authorization={authorization} setPositionID={setPositionID} />
        </Route>
        <Route path="/profile">
          <Profile authorization={authorization} initialPositionID={positionID} />
        </Route>
        <Route path="/interview">
          {authorization ? (
            <>
              <Route exact>
                <PurposePreface authorization={authorization} />
              </Route>
              <Route path="/start">
                <Interview authorization={authorization} />
              </Route>
              <Route path="/audio">
                <AudioRecordingScreen authorization={authorization} />
              </Route>
              <Route path="/complete">
                <InterviewComplete />
              </Route>
            </>
          ) : undefined}
        </Route>
        <Route path="/admin">
          <Admin authorization={authorization} />
        </Route>
      </Main>
    </Layout>
  );
};

export const App: FC = () => {
  return (
    <ApplicationStateProvider>
      <AuthProvider userManager={USER_MANAGER} autoSignIn={false}>
        <GlobalStyle />
        <Theme />
        <Route>
          <InnerApp />
        </Route>
      </AuthProvider>
    </ApplicationStateProvider>
  );
};
